import React, { forwardRef, useCallback, useContext, useState } from 'react';
import { Pressable, Text, View, Platform, ScrollView } from 'react-native';
import * as Animatable from 'react-native-animatable';
import { MaterialCommunityIcons, FontAwesome } from '@expo/vector-icons';
import moment from 'moment';
import styles from '@stylesheet';
import toTitleCase from '@hooks/toTitleCase';
import { debounce } from 'lodash';
import { Gesture, GestureDetector, GestureHandlerGestureEvent, GestureHandlerRootView, PanGestureHandlerEventPayload } from 'react-native-gesture-handler';
import Animated, { useAnimatedStyle, runOnJS, useAnimatedProps, useAnimatedReaction, useSharedValue } from 'react-native-reanimated';
import apis from '@api/api';
import { ToolbarContext } from '@hooks/toolbarContext';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useSelector } from 'react-redux';

function calculateTaskWidth(taskStart, taskEnd, projectRange) {
  const taskRange = taskEnd - taskStart;
  const widthValue = ((taskRange * 100) / projectRange);
  return widthValue;
}

function calculateTaskLeftOffset(taskStart, taskEnd, projectStart, projectRange) {
  const startOffset = taskStart - moment(projectStart).unix()
  const totalLeftOffset = ((startOffset * 100) / projectRange);
  return `${totalLeftOffset}%`;
}

function calculateTaskHeight(chartHeight, taskList) {
  return chartHeight / taskList - 3.5
}

interface RenderWPProps {
  item: any,
  index: number,
  projectStart: Date,
  projectEnd: Date,
  projectRange: number,
  chartHeight: number,
  taskList: any[],
  containerWidth: number
}

const RenderWP = forwardRef<ScrollView, RenderWPProps>((props, ref) => {
  const { item, index, projectStart, projectEnd, projectRange, chartHeight, taskList, containerWidth } = props;
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  // const [visible, setVisible] = useState(true);
  const [endDate, setEndDate] = useState(moment(item.end_date).format(globalDate));
  const [startDate, setStartDate] = useState(moment(item.start_date).format(globalDate));
  const itemWidth = useSharedValue(`${calculateTaskWidth(Number(moment(item.start_date).unix()), Number(moment(item.end_date).unix()), projectRange)}%`);
  const dynamicEnd = useSharedValue(moment(item.end_date).format(globalDate));
  const dateDifference = useSharedValue(0);
  const [hovered, setHovered] = useState(false);
  const [ availableHeight, setAvailableHeight ] = useState(0);
  const[dragActivated, setDragActivated] = useState(false);
  const { projectContext } = useContext(ToolbarContext);
  const _id = projectContext?.project?._id;
  const projectId = _id;
  

  const handleStartPanGesture = Gesture.Pan()
  .onChange((event) => {
      const { translationX } = event;
      // Calculate the new width based on the translation
      const newWidth = calculateTaskWidth(
        Number(moment(item.start_date).unix()) + (translationX / containerWidth) * projectRange,
        Number(moment(item.end_date).unix()), // Adjusted end date
        projectRange
      ) + '%';
      // Update the shared value with the new width
      itemWidth.value = newWidth;
    })

  const handleEndPanGesture = Gesture.Pan()
  .onChange((event) => {
      const { translationX } = event;
      // Calculate the new width based on the translation
      const dateDiff = (translationX / containerWidth) * projectRange;
      const newWidth = calculateTaskWidth(
        Number(moment(item.start_date).unix()),
        Number(moment(item.end_date).unix()) + (translationX / containerWidth) * projectRange, // Adjusted end date
        projectRange
      ) + '%';
      // Update the shared value with the new width
      itemWidth.value = newWidth;
      dateDifference.value = dateDiff;
      dynamicEnd.value = moment(item.end_date).add(dateDiff, 'seconds').format("DD MMM YY");
    })
    .onFinalize((event) => {
      apis.updateWorkPackageDue(item?._id, projectId, moment(dynamicEnd.value).toDate()).then((res) => {
        if (res.success)
        setEndDate(moment(item.end_date).add(dateDifference.value, 'seconds').format("DD MMM YY"))
      })
    });


  const tap = Gesture.LongPress().onFinalize(() => {
    console.log('long press ended');
    setDragActivated(false);
  })

  const startGesture = Gesture.Race(handleStartPanGesture, tap);
  const endGesture = Gesture.Race(handleEndPanGesture);

  const WPDrag = Animated.createAnimatedComponent(Pressable);

  return (
    <WPDrag
      onHoverIn={() => setHovered(true)}
      onPress={() => ref.current?.scrollToIndex({ index: index, animated: true })}
      onHoverOut={() => setHovered(false)}
      onLayout={({nativeEvent})=> setAvailableHeight(nativeEvent.layout.height)}
      // onLayout={(e) => { setLayoutWidth(current => [...current, e.nativeEvent?.layout.width]) }}
      style={{ flexDirection: "row", flexWrap: "nowrap", marginVertical: 2, width: itemWidth, backgroundColor: hovered ? 'rgb(99,210,244)' : 'rgb(90,192,234)', borderRadius: 5, minHeight: Platform.OS === 'web' ? (taskList.length > 8 ? 10 : "95%") : 30, maxHeight: "98%", left: calculateTaskLeftOffset(moment(item.start_date).unix(), moment(item.end_date).unix(), projectStart, projectRange), height: calculateTaskHeight(chartHeight, taskList) }}>
      <GestureDetector gesture={startGesture}>
        <Pressable style={styles.wpDrag}>
          <FontAwesome name="ellipsis-v" size={24} style={{ left: 5 }} color={"#ffffff60"} />
        </Pressable>
      </GestureDetector>
      <View
        // onPress={() => { setVisible(!visible) }} 
        style={{ flex: 10, alignSelf: "center", gap: 20 }}
      >
        <View style={{ flexDirection: 'column', flex: 1, overflow: "hidden" }}>
          {
            taskList.length < 8 &&
          <Text adjustsFontSizeToFit numberOfLines={1} style={{ maxWidth: "80%", textAlign: 'center', justifyContent: 'center', alignSelf: 'center', color: "white", fontWeight: "bold" }}>{toTitleCase(item.work_package_title) || <Text style={{ fontStyle: "italic", fontWeight: '400' }}>No Title</Text>}</Text>
          }
          {
            availableHeight > 70 &&
            <Animatable.View useNativeDriver animation={'slideInUp'} style={{ flex: 1 }}>
            <Text style={{ textAlign: "center", color: "white", fontWeight: "bold" }}>{moment(item.start_date).format("DD MMM YY")} - {dynamicEnd.value}</Text>
          </Animatable.View>
          }
        </View>
      </View>
      <GestureDetector gesture={endGesture}>
        <Pressable style={styles.wpDrag}>
          <FontAwesome name="ellipsis-v" style={{ right: -1 }} size={24} color={"#ffffff60"} />
        </Pressable>
      </GestureDetector>
    </WPDrag>
  )
});
export default RenderWP;